import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  clearWatch,
  watchPosition,
  type GeoPosition,
  type GeoWatchHandle,
} from '@/services/geolocationService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import type { TrackProfileAnalysis, TrackProfileLocationMatch } from '@/utils/trackProfileAnalysis'
import { findCurrentLocationOnTrackProfile } from '@/utils/trackProfileAnalysis'

export interface NavigationTarget {
  tourId: string
  trackClientId: string
  name: string
  analysis: TrackProfileAnalysis
}

export interface NavigationLocation {
  lat: number
  lon: number
  accuracyM: number | null
  speedMps: number | null
  headingDeg: number | null
  elevationM: number | null
  timestamp: number
}

const normalizeNullableNumber = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const positionToLocation = (position: GeoPosition): NavigationLocation => ({
  lat: position.coords.latitude,
  lon: position.coords.longitude,
  accuracyM: normalizeNullableNumber(position.coords.accuracy),
  speedMps: normalizeNullableNumber(position.coords.speed),
  headingDeg: normalizeNullableNumber(position.coords.heading),
  elevationM: normalizeNullableNumber(position.coords.altitude),
  timestamp: position.timestamp,
})

export const useNavigationStore = defineStore('navigation', () => {
  const target = ref<NavigationTarget | null>(null)
  const currentLocation = ref<NavigationLocation | null>(null)
  const followMap = ref(true)
  const isStarting = ref(false)
  const locationError = ref<string | null>(null)
  const watchHandle = ref<GeoWatchHandle | null>(null)

  const isActive = computed(() => target.value !== null)

  const matchedLocation = computed<TrackProfileLocationMatch | null>(() =>
    target.value && currentLocation.value
      ? findCurrentLocationOnTrackProfile(target.value.analysis, {
          lat: currentLocation.value.lat,
          lon: currentLocation.value.lon,
          accuracyM: currentLocation.value.accuracyM,
        })
      : null,
  )

  const remainingDistanceM = computed(() => {
    if (!target.value || !matchedLocation.value) return null
    return Math.max(0, target.value.analysis.totalDistanceM - matchedLocation.value.distanceM)
  })

  const offRouteDistanceM = computed(() => matchedLocation.value?.distanceFromTrackM ?? null)

  const stopLocationWatch = async () => {
    if (!watchHandle.value) return

    const handle = watchHandle.value
    watchHandle.value = null
    await clearWatch(handle)
  }

  const end = async () => {
    await stopLocationWatch()
    target.value = null
    currentLocation.value = null
    followMap.value = true
    isStarting.value = false
    locationError.value = null
  }

  const start = async (nextTarget: NavigationTarget) => {
    await stopLocationWatch()

    target.value = nextTarget
    currentLocation.value = null
    followMap.value = true
    locationError.value = null
    isStarting.value = true

    try {
      watchHandle.value = await watchPosition(
        { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 },
        (position) => {
          currentLocation.value = positionToLocation(position)
          locationError.value = null
        },
        (error) => {
          locationError.value = error.message
          logService.warn(LOG_CATEGORIES.MAP, 'Navigation location watch error', { error })
        },
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      locationError.value = message
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to start navigation location watch', { error })
    } finally {
      isStarting.value = false
    }
  }

  const setFollowMap = (enabled: boolean) => {
    followMap.value = enabled
  }

  return {
    target,
    currentLocation,
    followMap,
    isStarting,
    locationError,
    isActive,
    matchedLocation,
    remainingDistanceM,
    offRouteDistanceM,
    start,
    end,
    setFollowMap,
  }
})
